import React, { useState, useEffect, ReactNode } from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import Select from 'react-select'
import { formatPhoneDisplay } from 'utils/phoneUtils'

const COUNTRY_OPTIONS = [
  { value: '1', label: '🇺🇸 +1', name: 'USA' },
  { value: '507', label: '🇵🇦 +507', name: 'Panama' },
  { value: '52', label: '🇲🇽 +52', name: 'Mexico' },
  { value: '57', label: '🇨🇴 +57', name: 'Colombia' },
  { value: '58', label: '🇻🇪 +58', name: 'Venezuela' },
  { value: '54', label: '🇦🇷 +54', name: 'Argentina' },
  { value: '51', label: '🇵🇪 +51', name: 'Peru' },
  { value: '56', label: '🇨🇱 +56', name: 'Chile' },
  { value: '593', label: '🇪🇨 +593', name: 'Ecuador' },
  { value: '506', label: '🇨🇷 +506', name: 'Costa Rica' },
  { value: '502', label: '🇬🇹 +502', name: 'Guatemala' },
  { value: '503', label: '🇸🇻 +503', name: 'El Salvador' },
  { value: '504', label: '🇭🇳 +504', name: 'Honduras' },
  { value: '505', label: '🇳🇮 +505', name: 'Nicaragua' },
  { value: '1', label: '🇩🇴 +1', name: 'Dominican Republic' }, // DR is also +1
]

interface PhoneInputProps {
  id: string
  name: string
  label?: ReactNode
  value: string | null | undefined
  error?: string
  touched?: boolean
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  defaultCountryCode?: string
  onChange: (field: string, value: string) => void
  onBlur?: (field: string) => void
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  name,
  label,
  value,
  error,
  touched,
  placeholder,
  disabled,
  readOnly,
  defaultCountryCode = '1',
  onChange,
  onBlur,
}) => {
  const intl = useIntl()
  const resolvedPlaceholder =
    placeholder ??
    intl.formatMessage({id: 'common.placeholder.phoneNumber', defaultMessage: 'Phone number'})
  const [displayValue, setDisplayValue] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(
    COUNTRY_OPTIONS.find((opt) => opt.value === defaultCountryCode) || COUNTRY_OPTIONS[0]
  )

  // Split value into country code and local number
  useEffect(() => {
    if (value) {
      const digits = value.replace(/\D/g, '')
      const defaultCountry =
        COUNTRY_OPTIONS.find((opt) => opt.value === defaultCountryCode) || COUNTRY_OPTIONS[0]

      // While typing a non-US number, the saved value is country code + local digits.
      // Respect the user's selected country before treating 10 digits as a US local number.
      if (selectedCountry.value !== defaultCountry.value && digits.startsWith(selectedCountry.value)) {
        const localDigits = digits.slice(selectedCountry.value.length)
        const formatted = formatPhoneDisplay(localDigits, selectedCountry.value, false)
        setDisplayValue(formatted)
        return
      }

      // Prefer US for 10-digit numbers when the default country is US.
      if (defaultCountryCode === '1' && digits.length === 10) {
        setSelectedCountry(defaultCountry)
        const formatted = formatPhoneDisplay(digits, defaultCountry.value, false)
        setDisplayValue(formatted)
        return
      }

      // Treat 11-digit numbers starting with 1 as US with country code.
      if (digits.length === 11 && digits.startsWith('1')) {
        setSelectedCountry(defaultCountry)
        const formatted = formatPhoneDisplay(digits.slice(1), defaultCountry.value, false)
        setDisplayValue(formatted)
        return
      }

      // Attempt to find matching country code from start
      let found = false
      // Sort by length desc to match longest codes first (e.g. 507 before 5)
      const sortedOptions = [...COUNTRY_OPTIONS].sort((a, b) => b.value.length - a.value.length)

      for (const opt of sortedOptions) {
        if (digits.startsWith(opt.value)) {
          // Special case for +1: if it's +1 and length is 10, it might be the local number without prefix
          // But if it's +1 and length is 11, it's prefix+number
          if (opt.value === '1' && digits.length === 10) {
              // Stay as US but treat as local digits if it's a 10 digit number starting with something else?
              // Actually, if it starts with 1 and is 11 digits, it's US.
              // If it's 10 digits, it's local.
          }
          
          setSelectedCountry(opt)
          const localDigits =
            digits.startsWith('1') && digits.length > 10 ? digits.slice(1) : digits.slice(opt.value.length)
          const formatted = formatPhoneDisplay(localDigits, opt.value, false)
          setDisplayValue(formatted)
          found = true
          break
        }
      }

      if (!found) {
        // Fallback to default country
        const fallbackCountry = defaultCountry
        setSelectedCountry(fallbackCountry)
        const formatted = formatPhoneDisplay(digits, fallbackCountry.value, false)
        setDisplayValue(formatted)
      }
    } else {
      setDisplayValue('')
    }
  }, [value, defaultCountryCode, selectedCountry])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value
    let digits = input.replace(/\D/g, '')

    // For US, limit to 10 local digits
    if (selectedCountry.value === '1') {
      if (digits.length > 10 && digits.startsWith('1')) {
        digits = digits.slice(1)
      }
      if (digits.length > 10) {
        digits = digits.slice(0, 10)
      }
    }

    if (digits.length === 0) {
      setDisplayValue('')
      onChange(name, '')
      return
    }

    const formatted = formatPhoneDisplay(digits, selectedCountry.value, false)
    setDisplayValue(formatted)

    // Save full E.164-like number
    const toSave = `${selectedCountry.value}${digits}`
    onChange(name, toSave)
  }

  const handleCountryChange = (option: any) => {
    setSelectedCountry(option)
    // Re-format existing digits with new country
    const rawDigits = displayValue.replace(/\D/g, '')
    const formatted = formatPhoneDisplay(rawDigits, option.value, false)
    setDisplayValue(formatted)
    onChange(name, `${option.value}${rawDigits}`)
  }

  const customStyles = {
    control: (base: any) => ({
      ...base,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      minWidth: '100px',
      borderColor: error && touched ? '#f1416c' : '#dbdfe9',
    }),
  }

  return (
    <Form.Group>
      {label && <Form.Label htmlFor={id}>{label}</Form.Label>}
      <input 
        type="hidden" 
        name={name} 
        value={displayValue ? `${selectedCountry.value}${displayValue.replace(/\D/g, '')}` : ''} 
      />
      <InputGroup>
        <div className='phone-input-country'>
          <Select
            options={COUNTRY_OPTIONS}
            value={selectedCountry}
            onChange={handleCountryChange}
            styles={customStyles}
            isDisabled={disabled || readOnly}
            isSearchable={false}
          />
        </div>
        <Form.Control
          type="tel"
          id={id}
          disabled={disabled}
          placeholder={resolvedPlaceholder}
          value={displayValue}
          onChange={handleChange}
          onBlur={() => onBlur && onBlur(name)}
          readOnly={readOnly}
          isInvalid={!!error && !!touched}
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        />
      </InputGroup>
      {error && touched && (
        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  )
}
